import {useAppSelector} from '../../hooks/redux';
import api, {API_URL} from './api';

const patientApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getPatients: builder.query<any, string>({
      query: (query = '') => `patients?${query}`,
      providesTags: (result) =>
        result ? [...result.map(({id}) => ({type: 'Patients', id})), {type: 'Patients', id: 'LIST'}] : [{type: 'Patients', id: 'LIST'}],
    }),
    // Paginated search endpoint for table with proper caching
    searchPatientsPaginated: builder.query<
      {patients: any[]; total: number},
      {
        filter: string;
        page: number;
        pageSize: number;
        sort?: {id: string; desc: boolean} | null;
        search?: string;
      }
    >({
      query: ({filter, page, pageSize, sort, search}) => {
        const take = pageSize || 100;
        const skip = page * pageSize || 0;
        const orderBy = sort?.id;
        const desc = sort?.desc ? 'DESC' : 'ASC';
        const sortParam = orderBy ? `&orderBy=${orderBy}&desc=${desc}` : '';
        const searchParam = search ? `&name=${encodeURIComponent(search)}` : '';
        return `patients?${filter}${searchParam}&take=${take}&skip=${skip}${sortParam}`;
      },
      providesTags: (result) =>
        result?.patients
          ? [
              ...result.patients.map(({id}) => ({
                type: 'Patients' as const,
                id,
              })),
              {type: 'Patients', id: 'LIST'},
            ]
          : [{type: 'Patients', id: 'LIST'}],
    }),
    getPatient: builder.query({
      query: (id) => `patients/${id}`,
      providesTags: (result) => [{type: 'Patients', id: result?.id}],
    }),
    // Silent version for audit logs - returns null on error instead of showing toast
    getPatientSilent: builder.query({
      queryFn: async (id, _queryApi, _extraOptions, baseQuery) => {
        const result = await baseQuery(`patients/${id}`);
        if (result.error) {
          return {data: null};
        }
        return {data: result.data};
      },
    }),
    deletePatient: builder.mutation({
      query: (id) => ({
        url: `patients/${id}`,
        method: `DELETE`,
      }),
      invalidatesTags: [{type: 'Patients', id: 'LIST'}],
    }),
    updatePatient: builder.mutation({
      query: (formData) => ({
        url: `patients/${formData.id}`,
        method: `PUT`,
        body: formData,
      }),
      invalidatesTags: [{type: 'Patients', id: 'LIST'}],
    }),
    createPatient: builder.mutation({
      query: (patient) => ({
        url: `patients`,
        method: `POST`,
        body: patient,
      }),
      invalidatesTags: [{type: 'Patients', id: 'LIST'}],
    }),
  }),
});

export const useSearchPatientsByName = () => {
  const token = useAppSelector((s) => s.auth.token);
  return async (name: string) =>
    await fetch(`${API_URL}/patients-search?name=${encodeURIComponent(name)}`, {
      headers: {
        authorization: `Bearer ${token}`,
      },
    }).then((res) => res.json());
};

export const {
  useGetPatientsQuery,
  useSearchPatientsPaginatedQuery,
  useGetPatientQuery,
  useGetPatientSilentQuery,
  useDeletePatientMutation,
  useUpdatePatientMutation,
  useCreatePatientMutation,
} = patientApi;
